// =====================================================================
// AttachmentThumb — small 48×48 preview shown next to a file row.
// Used by Task Create (staged files) and Task Edit (uploaded files), so
// both lists get an image thumbnail / "PDF" badge / generic file icon
// using the same logic. Mirrors the preview pattern from DocUploadCard
// in EmployeeForm so the UX is consistent across the app.
//
//   <AttachmentThumb file={stagedFile} />                  // staged
//   <AttachmentThumb url={a.file_url} mime={a.mime_type}   // uploaded
//                    name={a.file_name} />
// =====================================================================

import { useEffect, useState } from "react";

type Props = {
  // Provide ONE of:
  //   - `file`: a still-staged File (pre-upload). The component creates
  //     a blob URL for previewing and revokes it on unmount.
  //   - `url`: an already-uploaded file URL (post-upload).
  file?: File;
  url?: string | null;
  mime?: string | null;
  name?: string;
  className?: string;
};

const IMAGE_MIME = /^image\//i;
const PDF_MIME = /pdf/i;
const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;
const PDF_EXT = /\.pdf$/i;

function detectKind(
  mime?: string | null,
  name?: string,
): "image" | "pdf" | "other" {
  if (mime) {
    if (IMAGE_MIME.test(mime)) return "image";
    if (PDF_MIME.test(mime)) return "pdf";
  }
  if (name) {
    if (IMAGE_EXT.test(name)) return "image";
    if (PDF_EXT.test(name)) return "pdf";
  }
  return "other";
}

export function AttachmentThumb({
  file,
  url,
  mime,
  name,
  className = "",
}: Props) {
  const resolvedMime = mime ?? file?.type ?? null;
  const resolvedName = name ?? file?.name ?? "";
  const kind = detectKind(resolvedMime, resolvedName);

  // Staged files don't have a public URL — create a transient blob URL
  // so we can (a) show a thumbnail for image files and (b) make the
  // preview clickable (opens the file in a new tab) for ANY file type,
  // matching the after-upload behavior. The URL is revoked on unmount
  // / when the file changes so we don't leak.
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setBlobUrl(null);
      return;
    }
    const u = URL.createObjectURL(file);
    setBlobUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  // The thumbnail itself is what we render INSIDE the tile.
  // The link target (`openHref`) is what clicking the tile opens.
  const openHref = file ? blobUrl : url || null;
  const previewUrl = openHref;

  const wrapperBase =
    `flex items-center justify-center h-12 w-12 shrink-0 rounded border ` +
    `border-gray-200 bg-gray-50 overflow-hidden ${className}`;
  // Hover affordance only when there's actually something to open.
  const wrapper = openHref
    ? `${wrapperBase} cursor-pointer hover:border-gray-300 hover:bg-gray-100 transition-colors`
    : wrapperBase;

  // Click handler — for uploaded files (i.e. `url` is set, not a local
  // `file`), the server may serve the resource with
  // `Content-Disposition: attachment`, which makes a plain
  // `<a target="_blank">` trigger a download instead of opening inline.
  // We fetch the bytes ourselves and open via a `blob:` URL, which has
  // no Content-Disposition header and always renders inline. If the
  // fetch fails (e.g. CORS blocked, network), we fall back to the
  // default link navigation so the user can still reach the file.
  const onTileClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Staged files already point at a `blob:` URL — let the browser
    // follow the link naturally; it always opens inline.
    if (file || !url) return;
    // Allow modifier-clicks (cmd/ctrl/middle-click for "open in new
    // window") to use the browser's default behavior, since those
    // intentionally want a real navigation.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
      return;
    }
    e.preventDefault();
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const inlineUrl = URL.createObjectURL(blob);
      // Open immediately in a new tab so the browser doesn't treat it
      // as a popup blocked from a non-user gesture.
      window.open(inlineUrl, "_blank", "noopener,noreferrer");
      // Revoke after the new tab has had time to load the resource.
      // Browsers keep the resource cached once loaded, so this is safe.
      setTimeout(() => URL.revokeObjectURL(inlineUrl), 60_000);
    } catch {
      // Fetch failed — fall back to a direct navigation. Worst case it
      // downloads (the existing behavior), best case the browser opens
      // it inline.
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  // Tiny helper so the four render paths below stay readable. When
  // `openHref` is null (no blob ready yet / no URL) we fall back to a
  // <div> so React still mounts something stable.
  const Tile = ({ children }: { children: React.ReactNode }) =>
    openHref ? (
      <a
        href={openHref}
        target="_blank"
        rel="noreferrer"
        onClick={onTileClick}
        className={wrapper}
        title={resolvedName}
      >
        {children}
      </a>
    ) : (
      <div className={wrapper} title={resolvedName}>{children}</div>
    );

  // ---- image ----------------------------------------------------------
  if (kind === "image" && previewUrl) {
    return (
      <Tile>
        <img
          src={previewUrl}
          alt={resolvedName}
          className="h-full w-full object-cover"
          // If the blob URL fails (rare), hide the broken img so the
          // tile still reads as a clickable preview.
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
      </Tile>
    );
  }

  // ---- pdf ------------------------------------------------------------
  if (kind === "pdf") {
    return (
      <Tile>
        <span className="text-red-600 text-[10px] font-bold tracking-tight">
          PDF
        </span>
      </Tile>
    );
  }

  // ---- other / fallback ----------------------------------------------
  // Generic paperclip — same icon the lists used before, so nothing
  // regresses for file types we can't preview (DOCX, XLSX, TXT, etc.).
  // Still clickable when we have a blob/public URL — opens / downloads
  // the file in a new tab.
  return (
    <Tile>
      <svg
        className="w-5 h-5 text-gray-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
        />
      </svg>
    </Tile>
  );
}

export default AttachmentThumb;
